import { Payment } from "@crypto-payment-gateway/models"
import { formatEther } from "ethers"
import {
	findDisplayNameAndImgForBankInJson,
	getPspOrBankId,
} from "../../../../../lib/utils"
import { formatPriceAmount } from "../../../../../utils/numbers"
import {
	CryptoParty,
	FiatParty,
	InboundArs,
	InboundCrypto,
	MovementCommissionDetails,
	OutboundArs,
	OutboundCrypto,
	PaymentHashTable,
	TableMovement,
	TableMovementType,
	TaxDescription,
	TaxType,
	Time,
} from "../types"
import { convertToTime, createErrorTime } from "../utils"
import { getTransactionType } from "./movementsUtils"

// -------------------- Mapping Functions --------------------
export function mapMovementsDataToTableMovements({
	transactions,
	paymentHashtable,
}: {
	payments: Payment[] | undefined
	transactions: any[]
	paymentHashtable: PaymentHashTable
}): TableMovement[] {
	const mappedMovements = transactions
		.map((tx) => mapTransactionsToMovements(tx, paymentHashtable))
		.filter((tx) => tx !== undefined) as TableMovement[]

	return mappedMovements
}

function mapTransactionsToMovements(
	tx: any,
	paymentHashtable: PaymentHashTable
): TableMovement | undefined {
	try {
		const type = getTransactionType(tx) ?? "INBOUND"
		const status = getTransactionStatus(tx, type)
		const time: Time = convertToTime(tx.creation_timestamp) ?? createErrorTime()

		// Get orderId from hashtable if payment_id exists
		const orderId = tx.payment_id ? paymentHashtable[tx.payment_id] || "-" : "-"

		return isTransferMovement(tx)
			? createFiatMovement(tx, type, status, time, orderId)
			: createCryptoMovement(tx, type, status, time, orderId)
	} catch (error) {
		console.error("Error mapping transaction:", error)
		console.error("Problematic transaction:", JSON.stringify(tx, null, 2))
		return undefined
	}
}

// -------------------- Party Creation Functions --------------------
function createFiatParty(tx: any): { sender: FiatParty; recipient: FiatParty } {
	return tx.transaction_data && !tx.transaction_data.PROCESSED
		? createUnprocessedFiatParty(tx)
		: tx.type === "INBOUND"
		? createInboundFiatParty(tx)
		: createOutboundFiatParty(tx)
}

function createCryptoParty(
	tx: any,
	type: TableMovementType
): {
	sender: CryptoParty
	recipient: CryptoParty
} {
	let sender: CryptoParty = { address: undefined, type: "crypto" }
	let recipient: CryptoParty = { address: undefined, type: "crypto" }

	switch (type) {
		case "INBOUND":
			sender = {
				address: tx.address,
				type: "crypto",
			}
			recipient = {
				address: tx.sender_address,
				type: "crypto",
			}
			break
		case "OUTBOUND":
			sender = {
				address: tx.sender_address,
				type: "crypto",
			}

			recipient = {
				address: tx.address,
				type: "crypto",
			}
			break
		default:
			break
	}

	return { sender, recipient }
}

function createDefaultBankEntity(isPsp = false): FiatParty["bank_entity"] {
	return {
		img: "",
		name: "",
		id: "",
		entity_type: isPsp ? "psp" : "bank",
	}
}

function createEmptyFiatParty(cvu = ""): FiatParty {
	return {
		cuit: "",
		titular: "",
		alias: "",
		cvu,
		bank_entity: createDefaultBankEntity(),
		type: "fiat",
	}
}

function createUnprocessedFiatParty(tx: any): {
	sender: FiatParty
	recipient: FiatParty
} {
	return {
		sender: createEmptyFiatParty(tx.sender_address),
		recipient: createEmptyFiatParty(tx.address),
	}
}

function createInboundFiatParty(tx: any): {
	sender: FiatParty
	recipient: FiatParty
} {
	const processed = tx.transaction_data?.PROCESSED

	const bankId = getPspOrBankId(processed.sender)
	const { bank_name, bank_img } = findDisplayNameAndImgForBankInJson(bankId[0])
	return {
		sender: {
			cuit: processed?.senderCuit,
			titular: processed?.senderTitular,
			alias: processed?.senderAlias,
			cvu: processed?.sender,
			bank_entity: {
				img: bank_img,
				name: bank_name,
				id: bankId[0],
				entity_type: processed?.senderIsPsp ? "psp" : "bank",
			},
			type: "fiat",
		},
		recipient: {
			cuit: processed?.recipientCuit,
			titular: processed?.recipientTitular,
			alias: processed?.recipientAlias,
			cvu: processed?.recipient,
			type: "fiat",
		},
	}
}

function createOutboundFiatParty(tx: any): {
	sender: FiatParty
	recipient: FiatParty
} {
	const processed = tx.transaction_data?.PROCESSED
	var recipient = tx.recipient_account
	if (!recipient) {
		recipient = processed
	}

	var bank_name
	var bank_img

	const bankId = getPspOrBankId(recipient.address ?? tx.address)

	if (bankId) {
		const res = findDisplayNameAndImgForBankInJson(bankId[0])
		bank_name = res.bank_name
		bank_img = res.bank_img
	}

	return {
		sender: {
			cuit: processed?.senderCuit,
			titular: processed?.senderTitular,
			alias: processed?.senderAlias,
			cvu: processed?.sender,
			type: "fiat",
		},
		recipient: {
			cuit: recipient.cuit,
			titular: recipient.name,
			alias: recipient.alias,
			cvu: recipient.address,
			bank_entity: {
				img: bank_img,
				name: bank_name,
				id: bankId && bankId[0],
				entity_type: recipient.psp_id ? "psp" : "bank",
			},
			type: "fiat",
		},
	}
}

// -------------------- Movement Creation Functions --------------------
function createFiatMovement(
	tx: any,
	type: TableMovementType,
	status: TableMovement["status"],
	time: Time,
	orderId: string
): InboundArs | OutboundArs {
	const { sender, recipient } = createFiatParty(tx)

	if (type === "INBOUND") {
		var { amountReceived, amountCredited, commission, percent } =
			calculateFiatAmounts(tx)

		return {
			id: tx.id,
			payment_id: tx.payment_id,
			type: "INBOUND",
			status,
			currency: "ARS",
			time,
			amountReceived,
			amountCredited,
			amountDisplayInTable: amountReceived,
			commission: {
				commission: { amount: commission.amount, currency: "ARS" },
				percent: percent,
				commissionTaxes: tx.commission_taxes
					? tx.commission_taxes.map((tax: TaxDescription) => ({
							tax_amount: tax.tax_amount,
							tax_type: tax.tax_type as TaxType,
							tax_rate: tax.tax_rate,
					  }))
					: undefined,
			},
			taxes: tx.taxes
				? {
						taxes: tx.taxes as TaxDescription[],
						total_tax_amount: tx.tax_amount,
				  }
				: undefined,
			orderId,
			store_name: tx.store_name,
			recipient: recipient,
			sender,
			movement_type: "transfer",
			customer_id: tx.customer_id,
			tableView: "movements",
		}
	} else {
		var amount = formatPriceAmount(tx.amount, undefined, "ARS")

		if (!amount) {
			amount = "-"
		}

		return {
			id: tx.id,
			type: "OUTBOUND",
			status,
			currency: "ARS",
			time,
			amountSent: amount,
			amountDisplayInTable: amount,
			recipient: recipient,
			sender,
			movement_type: "transfer",
			tableView: "movements",
		}
	}
}

function createCryptoMovement(
	tx: any,
	type: TableMovementType,
	status: TableMovement["status"],
	time: Time,
	orderId: string
): InboundCrypto | OutboundCrypto {
	const { sender, recipient } = createCryptoParty(tx, type)

	if (type === "INBOUND") {
		//calculate amount credited
		const amountRecevied = tx.amount
		const commission = tx.commission || 1
		const { amountAfterCommission: amountCredited, commissionAmount } =
			subtractCommissionCrypto(amountRecevied, commission)

		//now format amounts
		var formattedReceived = formatPriceAmount(
			formatEther(amountRecevied),
			undefined,
			tx.currency
		)
		var formattedCredited = formatPriceAmount(
			formatEther(amountCredited),
			undefined,
			tx.currency
		)

		var formattedCommissionAmount = formatPriceAmount(
			formatEther(commissionAmount),
			undefined,
			tx.currency
		)

		if (!formattedReceived || !formattedCredited) {
			formattedReceived = "-"
		}

		//Create the InboundCrypto object
		return {
			id: tx.id,
			payment_id: tx.payment_id,
			type: "INBOUND",
			status,
			currency: tx.currency,
			network: tx.network,
			time,
			amountReceived: formattedReceived,
			amountCommission: formattedCommissionAmount,
			amountCredited: formattedCredited,
			amountDisplayInTable: formattedReceived,
			commission: commission,
			orderId, // Use the orderId from hashtable
			store_name: tx.store_name,
			recipient: recipient as CryptoParty,
			sender: sender as CryptoParty,
			movement_type: "crypto",
			tableView: "movements",
		}
	}
	//This means this is OUTBOUND
	else {
		var amountSent = formatPriceAmount(
			formatEther(tx.amount),
			undefined,
			tx.currency
		)

		if (!amountSent) {
			amountSent = "-"
		}

		return {
			id: tx.id,
			type: "OUTBOUND",
			status,
			currency: tx.currency,
			network: tx.network,
			time,
			amountSent: amountSent,
			amountDisplayInTable: amountSent,
			gas: tx.gas,
			recipient: recipient,
			sender,
			movement_type: "crypto",
			tableView: "movements",
		}
	}
}

function calculateFiatAmounts(tx: any): MovementCommissionDetails & {
	amountReceived: string | undefined
	amountCredited: string | undefined
} {
	const amountReceived = tx.gross_amount
	const amountCredited = tx.amount
	const commissionPercent = tx.commission ?? 0
	const commissionAmount = tx.commission_amount ?? 0

	return {
		amountReceived: formatPriceAmount(amountReceived, undefined, "ARS"),
		amountCredited: formatPriceAmount(amountCredited, undefined, "ARS"),
		commission: { amount: commissionAmount, currency: "ARS" },
		commissionTaxes: tx.commission_taxes?.map((tax: any) => ({
			tax_amount: tax.tax_amount,
			tax_type: tax.tax_type as TaxType,
			tax_rate: tax.tax_rate,
		})),
		commission_tax_total_amount: tx.commission_tax_amount,
		percent: commissionPercent,
	}
}

// -------------------- Utility Functions --------------------
export function isTransferMovement(tx: any) {
	return tx.network === "POLLUX" && tx.currency === "ARS"
}

export function subtractCommissionCrypto(
	amount: string,
	commission: number
): { amountAfterCommission: bigint; commissionAmount: bigint } {
	const amountBigInt = BigInt(amount)
	const commissionBigInt = BigInt(commission)
	const commissionAmount = (amountBigInt * commissionBigInt) / BigInt(100)
	return {
		amountAfterCommission: amountBigInt - commissionAmount,
		commissionAmount,
	}
}

function getTransactionStatus(
	tx: any,
	type: "INBOUND" | "OUTBOUND"
): TableMovement["status"] {
	let status = tx.transaction_status
	if (!status) {
		return (status = "PROCESSED")
	}
	if (
		status === "PROCESSED" &&
		!tx.payment_id &&
		type === "INBOUND" &&
		!tx.customer_id
	) {
		return (status = "UNMATCHED")
	}
	return status
}
